/**
 * The stores management system: master arm, weapon selection, the trigger, and rearming.
 *
 * ## Master arm is the whole of "free flight stays weapons-cold"
 *
 * There is exactly one switch, {@link WeaponSystem.armed}, and everything hangs off it. Cold, the
 * gun will not fire, the seeker is unpowered, no missile will leave a rail, and — because the HUD
 * and the audio read the same flag — no weapons symbology is drawn and no lock tone is audible.
 * Free flight simply never arms it.
 *
 * That is deliberately not implemented as "the free-flight mode hides the weapons UI". A rule
 * enforced at the presentation layer is a rule that is one forgotten branch away from a trainer
 * with a working gun; a rule enforced at the source means the UI *cannot* show something the
 * aircraft is not doing, because it is reading the aircraft.
 *
 * ## Body axes, and where they get resolved
 *
 * A gun's muzzle and a missile's rails are positions on the airframe, in body axes. The
 * simulation is in sim-frame NED. Rather than hand this module a quaternion and have it
 * rediscover how the project's frames work, the caller passes the aircraft's three body axes
 * already expressed in NED, and a body-frame offset becomes one linear combination. It also means
 * this module has no opinion about attitude representation, which is what lets a network client
 * drive it from a transmitted basis just as easily.
 *
 * `updateFixed(dt)`: yes — {@link WeaponSystem.updateFixed}. Allocation-free after construction.
 */

import { Vector3 } from 'three';
import type { Rng } from '../../core/rng.js';
import { Cannon, makeGunInput, type GunInput } from './cannon.js';
import { MissileBattery } from './missile.js';
import { Seeker, makeSeekerInput, type SeekerInput } from './seeker.js';
import {
  computeStoresLoad,
  makeStoresLoad,
  type StoresLoad,
  type StoresRemaining,
} from './stores.js';
import { DEFAULT_GUN_RANGE_M, makeLcosInput, makeLcosSolution, solveLcos, type LcosSolution } from './lcos.js';
import type { WeaponsConfig } from './config.js';
import type { HitQueue, TargetList } from './engagement.js';

export type WeaponSelection = 'guns' | 'missile';

/** What the aircraft tells its weapons every tick. Owned by the caller and reused. */
export interface WeaponInput {
  /** Centre of gravity, sim-frame NED [m]. */
  readonly positionNed: Vector3;
  /** Body x (nose), expressed in sim-frame NED. Unit. */
  readonly forwardNed: Vector3;
  /** Body y (starboard), in NED. Unit. */
  readonly rightNed: Vector3;
  /** Body z (belly), in NED. Unit. */
  readonly downNed: Vector3;
  readonly velocityNed: Vector3;
  /** Body pitch rate [rad/s], nose-up positive — the sight's gyro. */
  pitchRateRadPerS: number;
  /** Body yaw rate [rad/s], nose-right positive. */
  yawRateRadPerS: number;
  densityKgPerM3: number;
  /** Ground elevation below the aircraft [m]. */
  groundElevationM: number;
  /** Is the trigger down? */
  trigger: boolean;
}

export function makeWeaponInput(): WeaponInput {
  return {
    positionNed: new Vector3(),
    forwardNed: new Vector3(1, 0, 0),
    rightNed: new Vector3(0, 1, 0),
    downNed: new Vector3(0, 0, 1),
    velocityNed: new Vector3(),
    pitchRateRadPerS: 0,
    yawRateRadPerS: 0,
    densityKgPerM3: 1.225,
    groundElevationM: 0,
    trigger: false,
  };
}

/** One missile just left a rail — the audio and the effects want to know. */
export interface LaunchEvent {
  readonly positionNed: Vector3;
  fired: boolean;
}

export class WeaponSystem {
  readonly cannon: Cannon | null;
  readonly missiles: MissileBattery | null;
  readonly seeker: Seeker | null;
  /** The gun sight's answer this frame. Meaningless when there is no gun. */
  readonly sight: LcosSolution = makeLcosSolution();

  /** Set true when a missile left a rail this tick; cleared by {@link clearEvents}. */
  readonly launch: LaunchEvent = { positionNed: new Vector3(), fired: false };

  private armedValue = false;
  private selectionValue: WeaponSelection = 'guns';
  private readonly gunInput: GunInput = makeGunInput();
  private readonly seekerInput: SeekerInput = makeSeekerInput();
  private readonly lcosInput = makeLcosInput();
  private readonly muzzleNed = new Vector3();
  private readonly stationNed = new Vector3();
  /** Edge detection: a missile leaves on the press, not once a tick while the trigger is held. */
  private triggerWasDown = false;
  /** Seconds until another missile may be launched. */
  private launchCooldownS = 0;

  /** The aircraft's own weapons block, kept so the stores load can be recomputed from it. */
  private readonly config: WeaponsConfig;
  /** Reused: what has been expended, recomputed only when a count actually changes. */
  private readonly storesLoad = makeStoresLoad();
  private readonly storesRemaining: StoresRemaining = { missiles: 0, rounds: 0 };

  constructor(config: WeaponsConfig, rng: Rng) {
    this.config = config;
    this.cannon = config.cannon === undefined ? null : new Cannon(config.cannon, rng);
    this.missiles = config.missile === undefined ? null : new MissileBattery(config.missile);
    this.seeker = config.missile === undefined ? null : new Seeker(config.missile);
    // An aircraft with only missiles selects missiles; one with only a gun selects the gun.
    this.selectionValue = config.cannon === undefined ? 'missile' : 'guns';
    this.storesRemaining.missiles = this.missiles?.remaining ?? 0;
    this.storesRemaining.rounds = this.cannon?.ammunition ?? 0;
  }

  /**
   * What is left aboard, and what its absence is doing to the aeroplane.
   *
   * Recomputed only when a count has actually changed, which is what makes this cheap enough to
   * ask for every tick: the gun fires a hundred times a second and the arithmetic is a handful of
   * multiplies, but the flight model's response to it inverts an inertia tensor, and that is not
   * something to do at 120 Hz for a number that did not move. See `stores.ts`.
   *
   * Returns null when nothing has changed since the last call, so the caller can skip the update
   * entirely — a null here means "the aeroplane is the same aeroplane it was last tick".
   */
  storesChanged(): StoresLoad | null {
    const missiles = this.missiles?.remaining ?? 0;
    const rounds = this.cannon?.ammunition ?? 0;
    if (missiles === this.storesRemaining.missiles && rounds === this.storesRemaining.rounds) {
      return null;
    }
    this.storesRemaining.missiles = missiles;
    this.storesRemaining.rounds = rounds;
    return computeStoresLoad(this.config, this.storesRemaining, this.storesLoad);
  }

  /** Does this aircraft carry anything at all? */
  get hasWeapons(): boolean {
    return this.cannon !== null || this.missiles !== null;
  }

  get armed(): boolean {
    return this.armedValue;
  }

  /**
   * Arm or safe the aircraft.
   *
   * Safeing clears the seeker and stops the gun; rounds and missiles already in the air keep
   * flying, because they have left and nothing about a switch in the cockpit can recall them.
   */
  setArmed(armed: boolean): void {
    if (armed === this.armedValue) return;
    this.armedValue = armed;
    if (!armed) {
      this.seeker?.reset();
      this.triggerWasDown = false;
    }
  }

  get selection(): WeaponSelection {
    return this.selectionValue;
  }

  /** Tab cycles the weapon. Only ever selects something the aircraft actually carries. */
  cycleSelection(): WeaponSelection {
    if (this.cannon === null || this.missiles === null) return this.selectionValue;
    this.selectionValue = this.selectionValue === 'guns' ? 'missile' : 'guns';
    // The seeker is only powered with the missile selected, so switching away drops the track.
    if (this.selectionValue === 'guns') this.seeker?.reset();
    return this.selectionValue;
  }

  /** Reload everything. The pause menu's REARM. */
  rearm(): void {
    this.cannon?.rearm();
    this.missiles?.rearm();
  }

  /**
   * Carry everything in the air through a floating-origin rebase.
   *
   * Ordnance is the one part of the combat system with no geodetic identity: targets re-derive
   * from a lat/lon anchor every tick and the terrain re-derives its tiles, so if the rounds do not
   * move with the origin the gun and the things it is shooting at end up in different frames. See
   * `Cannon.applyOriginShift` for why `previousNed` has to move too.
   *
   * The seeker is deliberately not touched: it holds bearings and rates, not positions, and both
   * are invariant under a translation of the frame.
   */
  applyOriginShift(deltaNorthM: number, deltaEastM: number, deltaDownM: number): void {
    this.cannon?.applyOriginShift(deltaNorthM, deltaEastM, deltaDownM);
    this.missiles?.applyOriginShift(deltaNorthM, deltaEastM, deltaDownM);
  }

  /** Everything back to the start of a sortie: full stores, empty sky, counters zeroed. */
  reset(): void {
    this.cannon?.reset();
    this.missiles?.reset();
    this.seeker?.reset();
    this.launch.fired = false;
    this.triggerWasDown = false;
    this.launchCooldownS = 0;
  }

  clearEvents(): void {
    this.launch.fired = false;
    this.missiles?.clearEvents();
  }

  /**
   * One physics step of the whole armament.
   *
   * Order matters: the seeker looks first, because the missile launched later this same tick must
   * be handed the target the seeker has *now*, not the one it had last tick.
   */
  updateFixed(dt: number, input: WeaponInput, targets: TargetList, hits: HitQueue): void {
    if (this.launchCooldownS > 0) this.launchCooldownS -= dt;

    // --- the seeker ---------------------------------------------------------------------------
    if (this.seeker !== null) {
      const seekerInput = this.seekerInput;
      seekerInput.active = this.armedValue && this.selectionValue === 'missile';
      seekerInput.positionNed.copy(input.positionNed);
      seekerInput.boresightNed.copy(input.forwardNed);
      seekerInput.velocityNed.copy(input.velocityNed);
      this.seeker.updateFixed(dt, seekerInput, targets);
    }

    // --- the gun ------------------------------------------------------------------------------
    if (this.cannon !== null) {
      const muzzle = this.cannon.config.muzzlePositionM;
      this.bodyToNed(input, muzzle[0], muzzle[1], muzzle[2], this.muzzleNed);

      const gun = this.gunInput;
      gun.firing = this.armedValue && this.selectionValue === 'guns' && input.trigger;
      gun.muzzleNed.copy(this.muzzleNed);
      gun.boreNed.copy(input.forwardNed);
      gun.boreRightNed.copy(input.rightNed);
      gun.boreUpNed.copy(input.downNed).multiplyScalar(-1);
      gun.carrierVelocityNed.copy(input.velocityNed);
      gun.densityKgPerM3 = input.densityKgPerM3;
      gun.groundElevationM = input.groundElevationM;
      this.cannon.updateFixed(dt, gun, targets, hits);

      this.solveSight(input);
    }

    // --- the rails ----------------------------------------------------------------------------
    if (this.missiles !== null) {
      const pressed = input.trigger && !this.triggerWasDown;
      if (
        pressed &&
        this.armedValue &&
        this.selectionValue === 'missile' &&
        this.launchCooldownS <= 0
      ) {
        this.fireMissile(input);
      }
      this.missiles.updateFixed(dt, targets, hits, input.densityKgPerM3, input.groundElevationM);
    }

    this.triggerWasDown = input.trigger;
  }

  /**
   * Send one, off the rail that still has a missile on it.
   *
   * A missile is launched even with no lock — it leaves the rail, flies ballistically and
   * self-destructs. That is what happens in the aeroplane, and refusing the shot would be a
   * silent no-op on a trigger press, which is the worst possible feedback.
   */
  private fireMissile(input: WeaponInput): void {
    const battery = this.missiles;
    if (battery === null || battery.remaining <= 0) return;

    const seeker = this.seeker;
    const locked = seeker !== null && seeker.state.status === 'locked' && seeker.state.inRange;
    const targetId = locked ? seeker.state.targetId : -1;

    // Rails are used up in order, so the stations empty symmetrically as the count falls.
    const stations = battery.config.stationsM;
    const station = stations[stations.length - battery.remaining] ?? stations[0];
    this.bodyToNed(input, station[0], station[1], station[2], this.stationNed);

    const round = battery.launch(
      this.stationNed,
      input.velocityNed,
      input.forwardNed,
      targetId,
    );
    if (round === null) return;

    this.launch.fired = true;
    this.launch.positionNed.copy(this.stationNed);
    // Half a second between shots: a salvo of four in one frame is a keypress bug, not a tactic.
    this.launchCooldownS = 0.5;
    // The seeker starts again — a fresh lock for the next round rather than a stale one.
    seeker?.reset();
  }

  /** Feed the sight, using the seeker's range when it has one. */
  private solveSight(input: WeaponInput): void {
    const cannon = this.cannon;
    if (cannon === null) return;
    const lcos = this.lcosInput;
    const seekerState = this.seeker?.state;
    lcos.rangeM =
      seekerState !== undefined && seekerState.status !== 'none' && seekerState.rangeM > 0
        ? seekerState.rangeM
        : DEFAULT_GUN_RANGE_M;
    lcos.muzzleVelocityMps = cannon.config.muzzleVelocityMps;
    lcos.inverseBc = cannon.roundInverseBc;
    lcos.densityKgPerM3 = input.densityKgPerM3;
    lcos.boreNed.copy(input.forwardNed);
    lcos.boreRightNed.copy(input.rightNed);
    lcos.boreDownNed.copy(input.downNed);
    lcos.carrierVelocityNed.copy(input.velocityNed);
    lcos.pitchRateRadPerS = input.pitchRateRadPerS;
    lcos.yawRateRadPerS = input.yawRateRadPerS;
    solveLcos(lcos, this.sight);
  }

  /** `cg + forward·x + right·y + down·z` — a body-frame offset in sim-frame NED. */
  private bodyToNed(input: WeaponInput, x: number, y: number, z: number, out: Vector3): Vector3 {
    out.copy(input.positionNed);
    out.addScaledVector(input.forwardNed, x);
    out.addScaledVector(input.rightNed, y);
    out.addScaledVector(input.downNed, z);
    return out;
  }
}
